/**
 * API Client utility for RxRooster frontend
 * Handles authentication, request/response interceptors, and error handling
 */

import { getBackendUrl } from '../utils/apiUrl';
import { codeOf, messageOf } from '../utils/httpErrorEnvelope';
import { canStepUp, requestStepUp } from './stepUpBroker';

/**
 * The backend signals a stale session with ForbiddenException('step_up_required'),
 * and AllExceptionsFilter then re-wraps the throw, so the marker arrives NESTED
 * at `message.message` -- never at the top level:
 *
 *   { success: false, statusCode: 403, path: '/billing/subscription/upgrade',
 *     message: { message: 'step_up_required', error: 'Forbidden', statusCode: 403 } }
 *
 * Confirmed against a real 403 off a FreshAuthGuard route on 2026-08-28. Reading
 * the body through the same envelope helpers the error path uses finds the marker
 * wherever it sits, and keeps working if the backend later throws a structured
 * `{ code: 'step_up_required' }` payload instead.
 */
const isStepUpRequired = (body) =>
  codeOf(body) === 'step_up_required' ||
  messageOf(body, null) === 'step_up_required';

// Support runtime override from mobile WebView
const API_BASE_URL = getBackendUrl();

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    console.log('API BASE_URL', API_BASE_URL);
  }

  getAuthHeaders() {
    // Prefer the key used by AuthContext ('authToken'), but support legacy keys too
    const token =
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async request(endpoint, options = {}, { allowStepUp = true } = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };


    try {
      const response = await fetch(url, config);

      // The backend answers 403 `step_up_required` when a session has not
      // authenticated recently enough to move money (FreshAuthGuard). Run the
      // re-auth modal and replay the call, so the user lands back on the action
      // they took instead of a dead error.
      //
      // `allowStepUp: false` on the replay bounds this to ONE retry: a backend
      // that answers step_up_required again must surface as an error rather
      // than loop the modal forever.
      if (
        response.status === 403 &&
        allowStepUp &&
        canStepUp() &&
        !endpoint.startsWith('/auth/step-up')
      ) {
        const body = await response.clone().json().catch(() => ({}));
        if (isStepUpRequired(body)) {
          const ok = await requestStepUp();
          if (ok) {
            // Re-read the token: getAuthHeaders picks up the re-issued one.
            return this.request(endpoint, options, { allowStepUp: false });
          }
        }
      }

      if (!response.ok) {
        // AllExceptionsFilter re-wraps the throw, so `message` holds the OBJECT
        // `exception.getResponse()` returned -- and an array of field strings
        // for a ValidationPipe rejection. Stringifying that put a raw JSON
        // envelope in front of the user wherever a component renders
        // `error.message`. `messageOf` unwraps the one level and always
        // returns a string.
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          messageOf(errorData, `HTTP ${response.status}: ${response.statusText}`),
        );
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return response;
    } catch (error) {
      // A caller that cancels its own request has not seen a failure.
      if (error?.name !== 'AbortError') {
        console.error(`API request failed: ${endpoint}`, error.message || error);
      }
      throw error;
    }
  }

  // `params` become the query string. An AbortSignal goes in the third
  // argument: passed as a param it was serialized into the URL
  // ("?signal=[object AbortSignal]") and the request could not be cancelled.
  async get(endpoint, params = {}, { signal } = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET',
      ...(signal ? { signal } : {}),
    });
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint, data) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...(data === undefined ? {} : { body: JSON.stringify(data) }),
    });
  }

  // File upload helper
  async upload(endpoint, formData) {
    const token =
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken');
    const headers = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.request(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

export default apiClient;
